import React, {useCallback, useEffect, useRef, useState} from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import routes from '../../../../../../../Routes/routes';
import {
    DEFAULT_LEFT,
    SEARCH_MODEL,
} from '../../../../../../../components/table/Model/TableModel';
import {
    DeleteIconsOptions,
    UpdateIconsOptions,
    CloneIconOptions, IconPlusFill, IconDepartment, DetailsIconsOptions,
} from '../../../../../../../components/buttons/IconButtons';
import DropDownMenuProfile from '../../../../../../../components/DropDown/DropDownMenuProfile';
import DropDownMenuItem from '../../../../../../../components/DropDown/DropDownMenuItem';
import { SimpleSearch } from '../../../../../../../reduxToolkit/Search/SearchApi';
import TableFunction from '../../../../../../../components/table/TableFunction';
import { permissionObj } from '../../../../../../../Helpers/permission';
import { CustomSwitch } from '../../../../../../../components/CustomSwitch';
import {ATTENDANCE_SUBVIEW_MODEL, TABS_ATTENDANCE} from "../../../Model/Model";
import {STATUS_MODEL} from "../../../../../../../enums/statusEnum";
import {EMPTY_VALUES, errorMsg} from "../../../../../../../Apis/functions";
import { toast } from 'react-toastify';
import {
    UpdateStatusPayrollPolicyGroup
} from "../../../../../../../reduxToolkit/PayrollPolicy/PayrollPolicyApi";
import ListDisplayWithTooltip from "../../../../../../../components/ListDisplayWithTooltip";
import {getConflictResolutionById, getStackMethodById} from "../../Global/CreateUpdatePolicy/Model/Model";
import RenderExpandedRow from "../SubComponents/RenderExpandedRow";
import {colors} from "../../../../../../../enums/colors";
import CustomChip from "../../../../../../../components/CustomChip";
import {DeleteItemsApi} from "../../../../../../../reduxToolkit/Commons/CommonsApi";
import {TABLES} from "../../../../../../../Apis/Tables";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

/**
 * Logic hook for the by-department overtime policies table.
 * @param {string} searchElement - Active search term.
 * @param {Function} setBodyForExport - Setter for the export request body.
 * @param {Object} option - Search model options.
 * @param {boolean} modified - Flag indicating table data has changed.
 * @param {Function} setModified - Setter for the modified flag.
 * @param {Array} selectedList - IDs of currently selected rows.
 * @param {Function} setSelectedList - Setter for the selected list.
 * @param {Function} registerAction - Registers external actions for the tab.
 * @param activeStep
 * @param subView
 * @returns {Object} State and handlers for the table component.
 */
const ByDepartmentTableApp = ({
    searchElement,
    setBodyForExport,
    option,
    modified,
    setModified,
    selectedList,
    setSelectedList,
    registerAction,
    activeStep,
    subView
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const tableId = 'tbl_bdt_by_department_overtime';
    const searchId = 'txt_bdt_by_department_overtime';

    const [results, setResults] = useState({ content: [], totalElements: 0 });
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [deleteModalShow, setDeleteModalShow] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [showActiveInactiveModal, setShowActiveInactiveModal] = useState(false);

    const canCreate = DEV || permission?.includes(permissionObj?.WEB_PAYROLL_POLICY_CREATE);
    const canUpdate = DEV || permission?.includes(permissionObj?.WEB_PAYROLL_POLICY_UPDATE);
    const canDelete = DEV || permission?.includes(permissionObj?.WEB_PAYROLL_POLICY_DELETE);
    const canClone = DEV || permission?.includes(permissionObj?.WEB_PAYROLL_POLICY_CLONE);

    const columns = [
        { field: 'name', label: 'name', fixed: true, left: DEFAULT_LEFT },
        {
            field: 'departments',
            label: 'departments',
            render: (value) => {
                return <ListDisplayWithTooltip
                    items={value}
                    renderItem={(u) => u.departmentName}
                    maxInline={2}
                    emptyValue={EMPTY_VALUES.DEFAULT}
                    icon={<IconPlusFill width={20} height={20}/>}
                    itemIcon={<IconDepartment className="icon-color-general-950"/>}
                />
            },
        },
        {
            field: 'conflictResolution',
            label: 'conflict_resolution',
            render: (value) => {
                return <CustomChip
                    background={colors.secondary100}
                    fontColor={colors.secondary700}
                    borderColor={colors.secondary150}
                    legend={getConflictResolutionById(value)?.name}
                    variant='compact'
                />
            },
        },
        {
            field: 'stackMethod',
            label: 'stack_method',
            render: (value) => {
                return value ? <CustomChip
                    background={colors.secondary100}
                    fontColor={colors.secondary700}
                    borderColor={colors.secondary150}
                    legend={getStackMethodById(value)?.name}
                    variant='compact'
                /> : EMPTY_VALUES.DEFAULT
            },
        },
        {
            field: 'status',
            label: 'status',
            render: (value, row) => (
                <CustomSwitch
                    id={`cb_bdt_status_${row?.id}`}
                    name="status"
                    checked={row?.statusId === STATUS_MODEL.ACTIVE_COMPANY}
                    showBorder={false}
                    className=""
                    disabled={!canUpdate}
                    onChange={() => {
                        setSelectedItem(row);
                        setShowActiveInactiveModal(true);
                    }}
                />
            ),
        },
    ];

    /**
     * Registers handleCreate and handleDelete with the parent tab on mount and
     * whenever the selection changes so the toolbar delete button stays in sync.
     */
    useEffect(() => {
        registerAction(subView, {handleCreate, handleDelete});
    }, [selectedList]);

    /**
     * Navigates to the create policy form, passing back-navigation context and scope flags.
     */
    const handleCreate = () => {
        navigate(routes.overtimePolicies.create,
            {
                state: {
                    backUrl: routes.attendance.main,
                    currentTab: activeStep,
                    currentSubView: subView,
                    isGlobal: false
                }
            });
    };

    /**
     * Opens the delete confirmation modal for the toolbar bulk-delete action.
     */
    const handleDelete = () => {
        setDeleteModalShow(true);
    };

    /**
     * Prepares a single item for deletion from the row menu.
     * @param {Object} item - The policy item to delete.
     */
    const handleDeleteItem = (item) => {
        setSelectedItem(item);
        setSelectedList([item.id]);
        setDeleteModalShow(true);
    };

    /**
     * Closes the delete confirmation modal and clears the selection.
     */
    const handleDeleteModalClose = () => {
        setDeleteModalShow(false);
        setSelectedList([]);
        setSelectedItem(null);
    };

    /**
     * Confirms deletion of selected items and refreshes the table state.
     */
    const handleDeleteSelected = () => {
        dispatch(DeleteItemsApi({tableName: TABLES.PAYROLL_POLICY_GROUP, body: selectedList})).then(
            ({
                 payload: {
                     data: {success, code},
                 },
             }) => {
                if (success === true) {
                    handleDeleteModalClose();
                    setModified(true);
                } else errorMsg(code);
            }
        );
    };

    /**
     * Renders the actions dropdown menu (update, delete, clone) for a table row.
     * @param {Object} item - The row data.
     * @param {number} index - Row index within the table.
     * @returns {JSX.Element} Dropdown menu with available actions.
     */
    const getActionsButtons = (item, index) => (
        <DropDownMenuProfile
            openMenu={openMenu}
            handleMenuToggle={handleMenuToggle}
            menuId={`menu-bdt-${item?.id}`}
        >
            {canUpdate && (
                <DropDownMenuItem
                    id={`btn_bdt_update_${item?.id}`}
                    index={index}
                    handleClose={handleClose}
                    name={t('update')}
                    click={() => navigate(`${routes.overtimeDepartmentPolicies.update}`,
                    { state: {
                        groupId: item?.id,
                        isGlobal: false,
                        backUrl: routes.attendance.main,
                        currentTab:  TABS_ATTENDANCE.OVERTIME_POLICIES.INDEX,
                        currentSubView: ATTENDANCE_SUBVIEW_MODEL.OVERTIME_POLICIES.BY_DEPARTMENT
                    } })}
                    icon={<UpdateIconsOptions />}
                />
            )}
            {!canUpdate &&
                <DropDownMenuItem
                    id={`btn_bdt_details_${item?.id}`}
                    index={index}
                    handleClose={handleClose}
                    name={t('details')}
                    click={() => navigate(`${routes.overtimeDepartmentPolicies.details}`,
                        { state: {
                                groupId: item?.id,
                                isGlobal: true,
                                backUrl: routes.attendance.main,
                                currentTab:  TABS_ATTENDANCE.OVERTIME_POLICIES.INDEX,
                                currentSubView: ATTENDANCE_SUBVIEW_MODEL.OVERTIME_POLICIES.BY_DEPARTMENT
                            } })}
                    icon={<DetailsIconsOptions />}
                />
            }
            {canClone && (
                <DropDownMenuItem
                    id={`btn_bdt_clone_${item?.id}`}
                    index={index}
                    handleClose={handleClose}
                    name={t('clone')}
                    click={() => navigate(`${routes.overtimeDepartmentPolicies.clone}`,
                    { state: {
                            groupId: item?.id,
                            isGlobal: false,
                            backUrl: routes.attendance.main,
                            currentTab:  TABS_ATTENDANCE.OVERTIME_POLICIES.INDEX,
                            currentSubView: ATTENDANCE_SUBVIEW_MODEL.OVERTIME_POLICIES.BY_DEPARTMENT
                        } })}
                    icon={<CloneIconOptions />}
                />
            )}
            {canDelete && (
                <DropDownMenuItem
                    id={`btn_bdt_delete_${item?.id}`}
                    index={index}
                    handleClose={handleClose}
                    name={t('delete')}
                    click={() => handleDeleteItem(item)}
                    icon={<DeleteIconsOptions />}
                    fontColor={'font-color-error-600'}
                    iconColor={'icon-color-error-code-600'}
                />
            )}
        </DropDownMenuProfile>
    );


    /**
     * Toggles the active/inactive status of the selected policy group and refreshes the table.
     */
    const handleStatusChange = () => {
        const isActive = selectedItem?.statusId === STATUS_MODEL.ACTIVE_COMPANY;
        const statusId = isActive ? STATUS_MODEL.IN_ACTIVE_COMPANY : STATUS_MODEL.ACTIVE_COMPANY;
        const successMsg = isActive ? 'has_been_deactivate_successfully' : 'has_been_activate_successfully';

        dispatch(UpdateStatusPayrollPolicyGroup({ id: selectedItem.id, statusId })).then(
            ({ payload: { data: { success, code } } }) => {
                setShowActiveInactiveModal(false);
                setSelectedItem(null);
                if (success === true) {
                    toast.success(t(successMsg));
                    setModified(true);
                } else {
                    errorMsg(code);
                }
            }
        );
    };

    /**
     * Fetches a page of data from the server and updates the export body.
     * @param {number} page - Page number to load.
     * @param {Object} params - Additional search parameters.
     * @param {string} params.searchElement - Search term filter.
     */
    const getData = (page, { searchElement } = {}) => {
        const body = SEARCH_MODEL.setObject({
            page,
            size: rowsPerPage,
            model: option,
            key: searchElement,
        });
        setBodyForExport(body);
        dispatch(SimpleSearch({ body })).then(({ payload: { data: { data, success } } }) => {
            if (success === true) setResults(data);
        });
    };

    /**
     * Expands a row to show its detail, collapsing the previously expanded row.
     * @param {Object} row - The row to expand.
     */
    const handleRowExpand = (row) => {
        if (row?.id !== expandedRowId) setExpandedRowId(row?.id);
    };

    const {
        t,
        handleCheckboxChange,
        handleDeleteAll,
        pagination,
        rowsPerPage,
        openMenu,
        handleMenuToggle,
        handleClose,
    } = TableFunction({
        selectedList,
        setSelectedList,
        data: results,
        labelRowsPerPage: 'rules_per_page',
        hasContainer: true,
        fixedPagination: false,
        searchElement,
        getData,
        modified,
        setModified,
    });

    /** Holds a stable reference to the expanded row's getData function. */
    const refreshExpandedRow = useRef(null);

    /**
     * Calls the expanded row's getData when the parent table signals a data change,
     * so the rules list stays in sync with the policy group status.
     */
    useEffect(() => {
        if (modified) refreshExpandedRow.current?.();
    }, [modified]);

    /**
     * Stable factory for the expanded row renderer. Empty deps prevent remounting
     * on every parent re-render; refreshRef provides a non-stale getData handle.
     */
    const renderExpandedRow = useCallback(
        (props) => <RenderExpandedRow
            {...props}
            refreshRef={refreshExpandedRow}
            setModified={setModified}
            canUpdate={canUpdate}
        />,
        []
    );

    return {
        t,
        DEV,
        permission,
        tableId,
        searchId,
        pagination,
        getActionsButtons,
        handleCheckboxChange,
        handleDeleteAll,
        results,
        setResults,
        rowsPerPage,
        columns,
        handleRowExpand,
        deleteModalShow,
        handleDeleteModalClose,
        handleDeleteSelected,
        selectedList,
        handleCreate,
        selectedItem,
        showActiveInactiveModal,
        setShowActiveInactiveModal,
        handleStatusChange,
        renderExpandedRow,
        canCreate,
        canDelete
    };
};

export default ByDepartmentTableApp;
